import React, { useEffect } from 'react';
import moment from 'moment';
import { useStore } from '../../../../../../stores/root';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../../services/general/error';
import GeneralService from '../../../../../../services/general';
import VesselRequisitionService from '../../../../../../services/vessels/requisitions/requisition';
import VesselRequisitionItemService from '../../../../../../services/vessels/requisitions/item';

// props
import { IVesselRequisitionResourceShortProps } from '../../../../../../props/vessels/requisitions/requisition';
import { IVesselRequisitionItemResourceShortProps } from '../../../../../../props/vessels/requisitions/item';

// components
import { DefaultButton, DetailsList, IColumn, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, SelectionMode, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import Text from '../../../../../typography/text';
import Label from '../../../../../typography/label';
import Details from '../../../../../typography/details';
import ValidationService from '../../../../../../services/validation';

interface RejectVesselRequisitionItemProps {
    requisition?: IVesselRequisitionResourceShortProps;
    items?: IVesselRequisitionItemResourceShortProps[];
    requisitionId?: string;
    itemIds?: string[];
    onDismissed(refresh?: boolean, status?: string): void;
}

const RejectVesselRequisitionItem: React.FC<RejectVesselRequisitionItemProps> = (props: RejectVesselRequisitionItemProps) => {
    const { banner, user } = useStore();

    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [requisition, setRequisition] = React.useState<IVesselRequisitionResourceShortProps | undefined>();
    const [selected, setSelected] = React.useState<IVesselRequisitionItemResourceShortProps[]>([]);
    const [justification, setJustification] = React.useState<string>("");
    const [error, setError] = React.useState<any>({});

    const columns: IColumn[] = [
        {
            key: 'details',
            name: 'Details',
            minWidth: 200,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack tokens={{ childrenGap: 10 }}>
                    <Stack tokens={{ childrenGap: 3 }}>
                        <Label size={'small'}>{item.goods.name}</Label>
                        {item.notes ? <Text style={{ fontStyle: 'italic' }} size={'small'}>Notes: {item.notes}</Text> : null}
                    </Stack>
                    <Stack tokens={{ childrenGap: 3 }}>
                        <Stack horizontal tokens={{ childrenGap: 3 }}>
                            <Text size={'small'}>Size</Text>
                            <Text size={'small'}>:</Text>
                            <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.length))} cm x {GeneralService.getNumberWithSeparator(Number(item.goods?.width))} cm x {GeneralService.getNumberWithSeparator(Number(item.goods?.height))} cm</Text>
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 3 }}>
                            <Text size={'small'}>Weight</Text>
                            <Text size={'small'}>:</Text>
                            <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.weightAmount))} {item.goods?.weightUnit || ""}</Text>
                        </Stack>
                    </Stack>
                </Stack>
            }
        },
        {
            key: 'purpose',
            name: 'Purpose',
            minWidth: 250,
            maxWidth: 250,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack>
                    <Text size={'small'}>{item.purpose || "-"}</Text>
                </Stack>
            }
        },
        {
            key: 'quantity',
            name: 'Quantity',
            minWidth: 125,
            maxWidth: 125,
            isMultiline: true,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack>
                    <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.quantityAmount))} {item.quantityUnit.name}</Text>
                </Stack>
            }
        }
    ]

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            let _requisition = undefined;
            if (props.requisitionId) {
                _requisition = await VesselRequisitionService.read(props.requisitionId);
            } else if (props.requisition) {
                _requisition = props.requisition;
            }

            let _selected: IVesselRequisitionItemResourceShortProps[] = [];
            if (_requisition && props.itemIds) {
                const _items = await VesselRequisitionItemService.retrieve(_requisition.id, `top=all`);
                _selected = _items.data.filter((item) => {
                    return (props.itemIds || []).indexOf(item.id) > -1;
                });
            } else if (props.items) {
                _selected = props.items;
            }

            setRequisition(_requisition);
            setSelected(_selected);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_requisition_items_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to get requisition items. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);

            const fd = new FormData();
            fd.append('justification', justification);

            selected.forEach((s) => {
                fd.append('ids[]', s.id);
            });

            await VesselRequisitionItemService.reject(requisition?.id || "", fd);
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    const isSubmitButtonDisabled = () => {
        if ((justification || "").trim() === "" || error.justification) {
            return true;
        }

        return false;
    }

    return <Panel headerText={'Reject Vessel Requisition Items'}
        isOpen={true}
        type={PanelType.custom}
        customWidth={'920px'}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Reject"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Rejecting requisition items ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded && requisition ? <>
                    <Text size={'small'}>Are you sure to reject all items below for <Label size={'xsmall'}>{requisition.vessel.name}</Label>:</Text>
                    <Stack className={'divider'}></Stack>
                    <Details title={'Name'} text={requisition.name} />
                    <Details title={'Vessel'} text={requisition.vessel.name} />
                    <Stack horizontal tokens={{childrenGap: 20}}>
                        <Details title={'Requested by'} text={requisition.createdBy.name} style={{width: '50%'}} />
                        <Details title={'Requested at'} text={moment(requisition.createdAt).format("DD/MM/YYYY HH:mm")} style={{width: '50%'}} />
                    </Stack>
                    <Stack className={'divider'}></Stack>
                    <DetailsList items={selected}
                        columns={columns}
                        compact={true}
                        selectionMode={SelectionMode.none} />
                    <Stack className={'divider'}></Stack>
                    <TextField label={'Justification'}
                        required
                        value={justification}
                        multiline
                        rows={5}
                        autoAdjustHeight
                        resizable={false}
                        errorMessage={error.justification}
                        onChange={(evt, value) => {
                            setJustification(value || "");
                            
                            const error = ValidationService.combination(value || "", ['required', 'limit'], {maxChars: 1000});
                            setError({justification: error.message})
                        }} />
                </> : null
            }
        </Stack>
    </Panel>
};

export default RejectVesselRequisitionItem;
